import PropTypes from 'prop-types';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { MoreHorizontal, ListPlus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function BatchesTable({
  batches,
  batchSortConfig,
  onBatchSort,
  onEditBatch,
  onDeleteBatch,
  onOpenSubscriptionModal,
  selectedBatches,
  onBatchSelection,
  onSelectAllBatches
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[50px]">
            <Checkbox
              checked={selectedBatches.length === batches.length && batches.length > 0}
              onCheckedChange={(checked) => onSelectAllBatches(checked)}
              aria-label="Select all batches"
            />
          </TableHead>
          <TableHead>Course Name (PK)</TableHead>
          <TableHead>Batch No (SK)</TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100"
            onClick={() => onBatchSort("capacity")}
          >
            Capacity {batchSortConfig.key === 'capacity' && (batchSortConfig.direction === 'asc' ? '↑' : '↓')}
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100"
            onClick={() => onBatchSort("count")}
          >
            Count {batchSortConfig.key === 'count' && (batchSortConfig.direction === 'asc' ? '↑' : '↓')}
          </TableHead>
          <TableHead>Assignment</TableHead>
          <TableHead>Course ID</TableHead>
          <TableHead>Course URL</TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100"
            onClick={() => onBatchSort("createdAt")}
          >
            Created At {batchSortConfig.key === 'createdAt' && (batchSortConfig.direction === 'asc' ? '↑' : '↓')}
          </TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {batches.length > 0 ? (
          batches.map((batch) => (
            <TableRow key={batch.SK}>
              <TableCell>
                <Checkbox
                  checked={selectedBatches.includes(batch.SK)}
                  onCheckedChange={(checked) => onBatchSelection(batch.SK, checked)}
                  aria-label={`Select batch ${batch.SK}`}
                />
              </TableCell>
              <TableCell>{batch.PK}</TableCell>
              <TableCell>{batch.SK}</TableCell>
              <TableCell>{batch.capacity}</TableCell>
              <TableCell>{batch.count}</TableCell>
              <TableCell>
                <div className="space-y-1">
                  {batch.batchStatus === 'empty' ? (
                    <Badge variant="outline" className="bg-gray-100">Empty</Badge>
                  ) : batch.batchStatus === 'assigned' ? (
                    <Badge variant="outline" className="bg-green-100">Assigned</Badge>
                  ) : batch.batchStatus === 'partially_assigned' ? (
                    <Badge variant="outline" className="bg-yellow-100">Partially Assigned</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-red-100">Unassigned</Badge>
                  )}
                  {batch.totalStudents !== undefined && (
                    <div className="text-xs text-gray-500">
                      {batch.assignedStudents || 0}/{batch.totalStudents} assigned
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>{batch.courseId || <span className="text-gray-400">None</span>}</TableCell>
              <TableCell>
                {batch.courseUrl || batch.courseId ? (
                  <a 
                    href={`${batch.courseUrl || "https://classplusapp.com/diy/courses/course-overview?id="}${batch.courseId || ""}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    View Course
                  </a>
                ) : "No URL available"}
              </TableCell>
              <TableCell>{new Date(batch.createdAt).toLocaleString()}</TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <span className="sr-only">Open menu</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEditBatch(batch)}>Edit</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onOpenSubscriptionModal(batch)}>
                      <ListPlus className="mr-2 h-4 w-4" />
                      Edit Subscription ID
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-red-600"
                      onClick={() => onDeleteBatch(batch)}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={10} className="text-center py-4">
              No batches found for this course
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

BatchesTable.propTypes = {
  batches: PropTypes.arrayOf(PropTypes.shape({
    SK: PropTypes.string.isRequired,
    PK: PropTypes.string.isRequired,
    capacity: PropTypes.number.isRequired,
    count: PropTypes.number.isRequired,
    courseId: PropTypes.string,
    courseUrl: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
    totalStudents: PropTypes.number,
    assignedStudents: PropTypes.number,
    unassignedStudents: PropTypes.number,
    batchStatus: PropTypes.oneOf(['empty', 'assigned', 'partially_assigned', 'unassigned']),
    assignment: PropTypes.string
  })).isRequired,
  batchSortConfig: PropTypes.shape({
    key: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(['asc', 'desc']).isRequired
  }).isRequired,
  onBatchSort: PropTypes.func.isRequired,
  onEditBatch: PropTypes.func.isRequired,
  onDeleteBatch: PropTypes.func.isRequired,
  onOpenSubscriptionModal: PropTypes.func.isRequired,
  selectedBatches: PropTypes.arrayOf(PropTypes.string).isRequired,
  onBatchSelection: PropTypes.func.isRequired,
  onSelectAllBatches: PropTypes.func.isRequired
}; 